/**
 * @file IntelligentFieldRenderers.tsx
 * @description Renderer components for the 6 intelligent field types:
 *   AUTOPOPULATE, HEADER, DISPLAY, RICHTEXT, IMAGE, COMPUTED
 *
 * Each is exported individually and consumed by DynamicFormField.tsx.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Zap, Eye, X, Image as ImageIcon, AlertCircle, Calculator,
  Bold, Italic, Underline, List, ListOrdered, Link2,
} from 'lucide-react';

import apiClient from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import { AppInlineLoader } from '@/modules/shared/components/ui/AppLoading';
import { Input } from '@/modules/shared/components/ui/input';
import type { FormFieldDefinition } from '../../types/formBuilder';
import type { FieldValue } from './dynamicFormFieldUtils';
import { uploadFile, getFileUrl, isAllowedImageFormat, isWithinSizeLimit } from './fileStorageHelper';

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  1. AUTOPOPULATE FIELD                                                  ║
// ╚══════════════════════════════════════════════════════════════════════════╝

interface AutoPopulateFieldProps {
  field: FormFieldDefinition;
  onChange: (fieldName: string, value: FieldValue) => void;
  disabled?: boolean;
}

export function AutoPopulateField({ field, onChange, disabled }: AutoPopulateFieldProps) {
  const { programId } = useParams<{ programId: string }>();
  const src = field.autoPopulateSource;

  const { data, isLoading } = useQuery({
    queryKey: ['autopopulate', programId, src?.entity, src?.field, src?.mappingKey],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<{ value: string }>>(
        `/programs/${programId}/data-source`,
        { params: { entity: src?.entity, field: src?.field, mappingKey: src?.mappingKey } },
      );
      return res.data?.data?.value ?? '';
    },
    enabled: !!programId && !!src?.entity && !!src?.field,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (data !== undefined) {
      onChange(field.internalName, data);
    }
  }, [data, field.internalName, onChange]);

  return (
    <div className="space-y-1">
      {field.label && (
        <label className="text-xs font-medium text-foreground flex items-center gap-1">
          <Zap className="w-3 h-3 text-primary" />
          {field.label}
        </label>
      )}
      <Input
        value={typeof data === 'string' ? data : ''}
        readOnly
        disabled={disabled}
        className="bg-muted/50 text-xs cursor-not-allowed"
        placeholder={isLoading ? 'Auto-populating' : 'Auto-populated'}
      />
      {field.helpText && (
        <p className="text-[11px] text-muted-foreground">{field.helpText}</p>
      )}
    </div>
  );
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  2. HEADER FIELD                                                        ║
// ╚══════════════════════════════════════════════════════════════════════════╝

interface HeaderFieldProps {
  field: FormFieldDefinition;
}

export function HeaderField({ field }: HeaderFieldProps) {
  const style = field.headerStyle;
  // Enforce 10–16px typography range per design rules
  const fontSize = Math.min(16, Math.max(10, style?.fontSize ?? 14));
  const textAlign = style?.align ?? 'left';

  return (
    <div
      className="font-semibold py-1"
      style={{ fontSize: `${fontSize}px`, textAlign, color: style?.color }}
    >
      {field.label || field.defaultValue || 'Section Header'}
    </div>
  );
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  3. DISPLAY FIELD (cross-form data)                                     ║
// ╚══════════════════════════════════════════════════════════════════════════╝

interface DisplayFieldProps {
  field: FormFieldDefinition;
}

export function DisplayField({ field }: DisplayFieldProps) {
  const { programId } = useParams<{ programId: string }>();
  const cfg = field.displayConfig;

  const { data, isLoading, error } = useQuery({
    queryKey: ['display-data', programId, cfg?.sourceFormCode, cfg?.fields],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<Record<string, string>>>(
        `/programs/${programId}/forms/display-data`,
        { params: { formCode: cfg?.sourceFormCode, fields: cfg?.fields?.join(',') } },
      );
      return res.data?.data ?? {};
    },
    enabled: !!programId && !!cfg?.sourceFormCode && (cfg?.fields?.length ?? 0) > 0,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-1">
      {field.label && (
        <label className="text-xs font-medium text-foreground flex items-center gap-1">
          <Eye className="w-3 h-3 text-muted-foreground" />
          {field.label}
        </label>
      )}
      <div className="rounded-md border border-border/50 bg-muted/30 p-3 space-y-1.5">
        {isLoading && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <AppInlineLoader label="Loading content" />
          </div>
        )}
        {error && (
          <div className="text-[11px] text-destructive flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Failed to load data
          </div>
        )}
        {!isLoading && !error && cfg?.fields?.map((f) => (
          <div key={f} className="flex items-baseline gap-2 text-xs">
            <span className="font-medium text-muted-foreground min-w-[100px]">{f}:</span>
            <span className="text-foreground">{data?.[f] ?? '—'}</span>
          </div>
        ))}
        {!isLoading && !error && (!cfg?.fields || cfg.fields.length === 0) && (
          <p className="text-[11px] text-muted-foreground italic">No fields configured.</p>
        )}
      </div>
      {field.helpText && (
        <p className="text-[11px] text-muted-foreground">{field.helpText}</p>
      )}
    </div>
  );
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  4. RICHTEXT FIELD                                                      ║
// ║  Lightweight contentEditable editor — no external deps required.        ║
// ║  Toolbar options driven by field.richTextToolbar config.                ║
// ╚══════════════════════════════════════════════════════════════════════════╝

interface RichTextFieldProps {
  field: FormFieldDefinition;
  value: FieldValue;
  onChange: (fieldName: string, value: FieldValue) => void;
  error?: string;
  disabled?: boolean;
}

const TOOLBAR_BUTTONS = [
  { id: 'bold', icon: Bold, command: 'bold', title: 'Bold' },
  { id: 'italic', icon: Italic, command: 'italic', title: 'Italic' },
  { id: 'underline', icon: Underline, command: 'underline', title: 'Underline' },
  { id: 'bullet', icon: List, command: 'insertUnorderedList', title: 'Bullet List' },
  { id: 'ordered-list', icon: ListOrdered, command: 'insertOrderedList', title: 'Ordered List' },
  { id: 'link', icon: Link2, command: 'createLink', title: 'Insert Link' },
] as const;

export function RichTextField({ field, value, onChange, error, disabled }: RichTextFieldProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalUpdate = useRef(false);
  const enabledTools = field.richTextToolbar ?? ['bold', 'italic', 'underline', 'bullet', 'link'];

  // Sync external value → editor (only when not internally editing)
  useEffect(() => {
    if (editorRef.current && !isInternalUpdate.current) {
      const html = typeof value === 'string' ? value : '';
      if (editorRef.current.innerHTML !== html) {
        editorRef.current.innerHTML = html;
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    isInternalUpdate.current = true;
    // Sanitize: strip script tags
    const raw = editorRef.current.innerHTML;
    const sanitized = raw.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    onChange(field.internalName, sanitized);
    requestAnimationFrame(() => { isInternalUpdate.current = false; });
  }, [field.internalName, onChange]);

  const execCommand = (cmd: string) => {
    if (disabled) return;
    if (cmd === 'createLink') {
      const url = window.prompt('Enter URL:');
      if (url) document.execCommand(cmd, false, url);
    } else {
      document.execCommand(cmd, false);
    }
    editorRef.current?.focus();
    handleInput();
  };

  const borderClass = error ? 'border-destructive' : 'border-border';

  return (
    <div className="space-y-1">
      {field.label && (
        <label className="text-xs font-medium text-foreground">
          {field.label}
          {field.mandatory && <span className="text-destructive ml-0.5">*</span>}
        </label>
      )}
      <div className={`rounded-md border ${borderClass} overflow-hidden`}>
        {/* Toolbar */}
        <div className="flex items-center gap-0.5 px-2 py-1 border-b bg-muted/30">
          {TOOLBAR_BUTTONS.filter((b) => enabledTools.includes(b.id as typeof enabledTools[number])).map((btn) => (
            <button
              key={btn.id}
              type="button"
              title={btn.title}
              onClick={() => execCommand(btn.command)}
              disabled={disabled}
              className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-40"
            >
              <btn.icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
        {/* Editor */}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleInput}
          className="min-h-[80px] max-h-[200px] overflow-auto px-3 py-2 text-xs outline-none"
          data-placeholder={field.placeholder ?? 'Start typing...'}
        />
      </div>
      {error && <p className="text-[11px] text-destructive">{error}</p>}
      {field.helpText && <p className="text-[11px] text-muted-foreground">{field.helpText}</p>}
    </div>
  );
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  5. IMAGE UPLOAD FIELD (S3-backed, key-only storage)                    ║
// ╚══════════════════════════════════════════════════════════════════════════╝

interface ImageUploadFieldProps {
  field: FormFieldDefinition;
  value: FieldValue;
  onChange: (fieldName: string, value: FieldValue) => void;
  error?: string;
  disabled?: boolean;
}

export function ImageUploadField({ field, value, onChange, error, disabled }: ImageUploadFieldProps) {
  const cfg = field.imageConfig;
  const maxSizeMB = cfg?.maxSizeMB ?? 5;
  const allowedFormats = cfg?.allowedFormats ?? ['png', 'jpg', 'jpeg', 'webp'];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (file: File) => uploadFile(file, 'form-images'),
    onSuccess: (result) => {
      onChange(field.internalName, result.fileKey);
      setLocalError(null);
    },
    onError: () => {
      setLocalError('Upload failed. Please try again.');
    },
  });

  const handleFile = (file: File) => {
    if (!isAllowedImageFormat(file, allowedFormats)) {
      setLocalError(`Allowed formats: ${allowedFormats.join(', ')}`);
      return;
    }
    if (!isWithinSizeLimit(file, maxSizeMB)) {
      setLocalError(`Max file size: ${maxSizeMB} MB`);
      return;
    }
    setLocalError(null);
    mutation.mutate(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleClear = () => {
    onChange(field.internalName, '');
    setLocalError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fileKey = typeof value === 'string' ? value : '';
  const previewUrl = fileKey ? getFileUrl(fileKey) : '';
  const displayError = error || localError;
  const borderClass = displayError ? 'border-destructive' : 'border-dashed border-border';
  const previewW = cfg?.width ? `${cfg.width}px` : '100%';
  const previewH = cfg?.height ? `${cfg.height}px` : 'auto';

  return (
    <div className="space-y-1">
      {field.label && (
        <label className="text-xs font-medium text-foreground">
          {field.label}
          {field.mandatory && <span className="text-destructive ml-0.5">*</span>}
        </label>
      )}

      {fileKey && previewUrl ? (
        <div className="relative inline-block">
          <img
            src={previewUrl}
            alt={field.label ?? 'Uploaded image'}
            className="rounded-md border border-border object-cover"
            style={{ width: previewW, maxWidth: '100%', height: previewH, maxHeight: '200px' }}
          />
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-1 right-1 p-0.5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              title="Remove image"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ) : (
        <div
          className={`flex flex-col items-center justify-center gap-2 p-4 rounded-md border-2 ${borderClass} cursor-pointer hover:bg-muted/20 transition-colors`}
          onClick={() => !disabled && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {mutation.isPending ? (
            <AppInlineLoader label="Loading content" />
          ) : (
            <>
              <ImageIcon className="w-5 h-5 text-muted-foreground" />
              <p className="text-[11px] text-muted-foreground">
                Click or drag to upload
              </p>
              <p className="text-[11px] text-muted-foreground">
                {allowedFormats.join(', ').toUpperCase()} • Max {maxSizeMB} MB
              </p>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={allowedFormats.map((f) => `.${f}`).join(',')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
        disabled={disabled || mutation.isPending}
      />

      {displayError && <p className="text-[11px] text-destructive">{displayError}</p>}
      {field.helpText && <p className="text-[11px] text-muted-foreground">{field.helpText}</p>}
    </div>
  );
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  6. COMPUTED FIELD (formula-based read-only)                            ║
// ╚══════════════════════════════════════════════════════════════════════════╝

interface ComputedFieldProps {
  field: FormFieldDefinition;
  computedValue: FieldValue;
}

export function ComputedField({ field, computedValue }: ComputedFieldProps) {
  const cfg = field.computedConfig;
  let display = '';

  if (computedValue !== null && computedValue !== undefined) {
    if (cfg?.outputType === 'NUMBER' && typeof computedValue === 'number') {
      display = cfg.precision !== undefined
        ? computedValue.toFixed(cfg.precision)
        : String(computedValue);
    } else {
      display = String(computedValue);
    }
  }

  return (
    <div className="space-y-1">
      {field.label && (
        <label className="text-xs font-medium text-foreground flex items-center gap-1">
          <Calculator className="w-3 h-3 text-primary" />
          {field.label}
        </label>
      )}
      <Input
        value={display}
        readOnly
        className="bg-muted/50 text-xs cursor-not-allowed"
        placeholder="Computed..."
      />
      {field.helpText && (
        <p className="text-[11px] text-muted-foreground">{field.helpText}</p>
      )}
    </div>
  );
}
